import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { AIProviderCode } from '@ellie/shared';
import { DEFAULT_AI_PROVIDER, DEFAULT_AI_MODEL } from '@ellie/shared';

interface AIProviderSettings {
  provider: AIProviderCode;
  model: string;
  api_key_env_var: string;
}

interface ActiveProviderState {
  provider: AIProviderCode;
  model: string;
  loading: boolean;
}

/**
 * Fetches the admin-selected AI provider/model from the `app_settings` table.
 * Falls back to Anthropic Sonnet defaults while loading or on error.
 */
export function useActiveProvider(): ActiveProviderState {
  const [provider, setProvider] = useState<AIProviderCode>(DEFAULT_AI_PROVIDER);
  const [model, setModel] = useState(DEFAULT_AI_MODEL);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const { data, error } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'ai_provider')
        .single();

      if (cancelled) return;

      if (!error && data?.value) {
        const val = data.value as AIProviderSettings;
        if (val.provider) setProvider(val.provider);
        if (val.model) setModel(val.model);
      }

      setLoading(false);
    }

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  return { provider, model, loading };
}
